import { Capacitor } from '@capacitor/core'

export type Platform = 'ios-native' | 'android-native' | 'web'

let cachedPlatform: Platform | null = null

export function getPlatform(): Platform {
  if (cachedPlatform) return cachedPlatform

  const isNative = Capacitor.isNativePlatform()
  const nativePlatform = Capacitor.getPlatform()

  if (isNative && nativePlatform === 'ios') {
    cachedPlatform = 'ios-native'
  } else if (isNative && nativePlatform === 'android') {
    cachedPlatform = 'android-native'
  } else {
    cachedPlatform = 'web'
  }

  return cachedPlatform
}

/**
 * Returns true on iOS/Android native (Capacitor) platforms.
 *
 * Used specifically to gate Capacitor-only sync triggers:
 * - Network.addListener (Capacitor Network plugin)
 * - App.addListener (Capacitor App state plugin)
 *
 * The Dexie queue itself (nativeQueueManager) works on all platforms —
 * see shouldQueueMutation().
 */
export function shouldUseNativeQueue(): boolean {
  const platform = getPlatform()
  return platform === 'ios-native' || platform === 'android-native'
}

export function isIosNative(): boolean {
  return getPlatform() === 'ios-native'
}

export function isAndroidNative(): boolean {
  return getPlatform() === 'android-native'
}

export function isNativePlatform(): boolean {
  const platform = getPlatform()
  return platform === 'ios-native' || platform === 'android-native'
}

/**
 * Returns true when mutations should be routed through the Dexie queue
 * (nativeQueueManager) on all platforms — web and native alike.
 *
 * Always true: the unified queue provides offline buffering, deduplication,
 * retry with exponential backoff, and blob storage for file uploads.
 * On success, Mercure SSE invalidates React Query (no direct invalidation).
 */
export function shouldQueueMutation(): boolean {
  return true
}
